/** Contract for the admin export engine (SPEC.md §5.6 reporting). */
import { z } from 'zod';
import { departmentSchema, ticketPrioritySchema, ticketStatusSchema } from '../enums.js';
import { propertyDateKeySchema } from './date-key.js';
export const exportFormatSchema = z.enum(['xlsx', 'csv']);
/** `YYYY-MM-DD` in property time — the one definition, shared with the Closed archive. */
const propertyDateSchema = propertyDateKeySchema;
/**
 * Both bounds are **inclusive** calendar dates: `from=2026-08-01&to=2026-08-01`
 * exports that single day in full. The server widens them to a half-open UTC range.
 */
export const exportTicketsQuerySchema = z
    .object({
    format: exportFormatSchema.default('xlsx'),
    from: propertyDateSchema,
    to: propertyDateSchema,
    department: departmentSchema.optional(),
    status: ticketStatusSchema.optional(),
})
    .refine((value) => value.from <= value.to, {
    // Lexicographic comparison is sound for zero-padded ISO dates.
    message: 'from must not be after to',
    path: ['from'],
});
/**
 * One row of the export.
 *
 * Durations are whole minutes rather than a formatted string so the spreadsheet can
 * be sorted and averaged. Null means the ticket has not reached that state yet, which
 * is different from zero and must not be flattened into it.
 */
export const ticketExportRowSchema = z.object({
    reference: z.number().int(),
    title: z.string(),
    status: ticketStatusSchema,
    priority: ticketPrioritySchema,
    department: departmentSchema,
    locationName: z.string().nullable(),
    assigneeName: z.string().nullable(),
    reporterName: z.string().nullable(),
    /** Report time — `createdAt`, which carries the clamped client `reportedAt` (D18). */
    reportedAt: z.string(),
    acknowledgedAt: z.string().nullable(),
    resolvedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
    /** reportedAt → acknowledgedAt. */
    minutesToAcknowledge: z.number().int().nullable(),
    /** reportedAt → resolvedAt, inclusive of any time on hold. */
    minutesToResolve: z.number().int().nullable(),
    /**
     * Time the resolve clock was paused, so the two figures above can be reconciled.
     *
     * Never null, unlike the durations above: every ticket has a hold total, and for
     * one that was never paused it is a measured `0`. The distinction the other two
     * fields draw — "not reached yet" versus "took no time" — has no counterpart here.
     */
    minutesOnHold: z.number().int(),
    breachedAck: z.boolean(),
    breachedResolve: z.boolean(),
});
/** Column order for both spreadsheet and CSV output — one definition, two writers. */
export const TICKET_EXPORT_COLUMNS = [
    { key: 'reference', header: 'Ref' },
    { key: 'title', header: 'Title' },
    { key: 'status', header: 'Status' },
    { key: 'priority', header: 'Priority' },
    { key: 'department', header: 'Department' },
    { key: 'locationName', header: 'Location' },
    { key: 'assigneeName', header: 'Assigned to' },
    { key: 'reporterName', header: 'Reported by' },
    { key: 'reportedAt', header: 'Reported at' },
    { key: 'acknowledgedAt', header: 'Acknowledged at' },
    { key: 'resolvedAt', header: 'Resolved at' },
    { key: 'closedAt', header: 'Closed at' },
    { key: 'minutesToAcknowledge', header: 'Minutes to acknowledge' },
    { key: 'minutesToResolve', header: 'Minutes to resolve' },
    { key: 'minutesOnHold', header: 'Minutes on hold' },
    { key: 'breachedAck', header: 'Ack SLA breached' },
    { key: 'breachedResolve', header: 'Resolve SLA breached' },
];
//# sourceMappingURL=reports.js.map